import Settings from "./Settings";
import { AuthToken } from "./AuthToken";

import ProcessingResult from "../framework/ProcessingResult";

import TransformationMapper from "./mappers/TransformationMapper";
import DataTableReadMapper from "./mappers/DataTableReadMapper";
import GroupFilterMapper from "./mappers/GroupFilterMapper";
import DataTableRelationMapper from "./mappers/DataTableRelationMapper";

import DataTableReadDto from "../classes/dtos/pathcommonmodule/DataTableReadDto";
import DataTableRelationDto from "../classes/dtos/pathcommonmodule/DataTableRelationDto";

import GetPathDataTableDetailResult from "../classes/api/result/pathdatatablemodule/GetPathDataTableDetailResult";
import UpdatePathDataTableDetailResult from "../classes/api/result/pathdatatablemodule/UpdatePathDataTableDetailResult";

import GetPathDataTableCapabilitiesResult from "../classes/api/result/pathdatatablemodule/GetPathDataTableCapabilitiesResult";
import PathDataTableDetailsResult from "../classes/api/result/pathdatatablemodule/PathDataTableDetailsResult";

import PathDataTableMappingDto from "../classes/dtos/pathdatatablemodule/PathDataTableMappingDto";
import ScheduleDto from "../classes/dtos/schedulermodule/ScheduleDto";

import FieldReferenceIdAndKeyDto from "../classes/dtos/FieldReferenceIdAndKeyDto";
import FieldTypeReferenceDto from "../classes/dtos/FieldTypeReferenceDto";
import PathDataTableMappingCapabilitiesDto from "../classes/dtos/pathdatatablemodule/PathDataTableMappingCapabilitiesDto";

const PathDataTableApi = {

    GetPathDataTableDetailAsync: async function (pathId: number): Promise<ProcessingResult<GetPathDataTableDetailResult>> {
        let authSession = await AuthToken.get();

        const response = await fetch(Settings.api_url + "/PathDataTable/GetPathDataTableDetail?pathId=" + pathId.toString(), {
            method: "Get",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            }
        });

        if (response.status === 200) {
            let data = await response.json();
            let source = DataTableReadMapper.ToDto(data.source);
            let linkedDataTables = DataTableRelationMapper.ToDtos(data.linkedDataTables);
            let mappings = data.mappings.map((e: any) => new PathDataTableMappingDto(e.fieldReferenceId, e.transformationLogicId, e.transformationParameters, e.aggregationGroupBy, e.aggregationLogicId, e.aggregationParameters, e.displayName, e.isSelected, GroupFilterMapper.RowFiltersToDtos(e.filters), GroupFilterMapper.DeclaredVariablesFromResponse(e.declaredVariables)));
            let schedule = new ScheduleDto(data.schedule.isActive, data.schedule.scheduleType, data.schedule.interval, data.schedule.time);
            return ProcessingResult.Ok(new GetPathDataTableDetailResult(data.id, data.name, source, linkedDataTables, mappings, schedule, data.lastVersionId, data.publishedVersionId, data.advancedFilterMode ?? false));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    UpdatePathDataTableDetailAsync: async function (pathId: number, name: string, mappings: PathDataTableMappingDto[], source: DataTableReadDto, linkedDataTables: DataTableRelationDto[], schedule: ScheduleDto, advancedFilterMode: boolean, publish: boolean): Promise<ProcessingResult<UpdatePathDataTableDetailResult>>
    {
        let authSession = await AuthToken.get();

        let time = (schedule.Time / (60 * 1000)) - new Date().getTimezoneOffset();

        const response = await fetch(Settings.api_url + "/PathDataTable/UpdatePathDataTableDetail", {
            method: "Post",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            },
            body: JSON.stringify({
                'pathId': pathId,
                'name': name,
                'source': DataTableReadMapper.ToPayload(source),
                'linkedDataTables': DataTableRelationMapper.ToPayloads(linkedDataTables),
                'mappings': mappings.map((e) => ({ fieldReferenceId: e.FieldReferenceId, transformationLogicId: e.TransformationLogicId, transformationParameters: e.TransformationParameters, aggregationGroupBy: e.AggregationGroupBy, aggregationLogicId: e.AggregationLogicId, aggregationParameters : e.AggregationParameters, displayName: e.DisplayName, isSelected: e.IsSelected, filters: GroupFilterMapper.RowFiltersToPayload(e.Filters), declaredVariables: GroupFilterMapper.DeclaredVariablesToPayload(e.DeclaredVariables) })),
                'schedule': {
                    'isActive': schedule.IsActive,
                    'scheduleType': schedule.Type,
                    'interval': schedule.Interval,
                    'time': time
                },
                'advancedFilterMode': advancedFilterMode,
                'Publish': publish
            })
        });

        if (response.status === 200)
        {
            let data = await response.json();
            return ProcessingResult.Ok(new UpdatePathDataTableDetailResult(data.id, data.isPublished));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    },

    GetPathDataTableCapabilitiesAsync: async function (MainDataTable: DataTableReadDto, LinkedDataTables: DataTableReadDto[]): Promise<ProcessingResult<GetPathDataTableCapabilitiesResult>> {
        let authSession = await AuthToken.get();

        const request = {
            mainDataTable: DataTableReadMapper.ToPayload(MainDataTable),
            linkedDataTables: LinkedDataTables.map((e) => DataTableReadMapper.ToPayload(e))
        };

        let url = `/PathDataTable/GetPathDataTableCapabilities`;

        const response = await fetch(Settings.api_url + url, {
            method: "Post",
            mode: "cors",
            cache: "no-cache",
            credentials: 'include',
            headers: {
                "Content-Type": "application/json",
                "Authorization": "Bearer " + authSession.Token
            },
            body: JSON.stringify(request)
        });

        if (response.status === 200) {
            let data = await response.json();
            let fields = data.fields.map((x: any) => new PathDataTableDetailsResult(DataTableReadMapper.ToDto(x.dataTable)!, x.fields.map((e: any) => new FieldReferenceIdAndKeyDto(e.id, e.sourceKey, e.datasetKey, e.name, new FieldTypeReferenceDto(e.type.type, e.type.isMultiple, e.type.typeLabel, e.type.isNullable, e.type.maxLength, e.type.isReadOnly, e.type.isCalculated, e.type.isBusinessRequired, e.type.isPrimaryKey, e.type.originTypeLabel, e.type.linkTableId)))));
            let mappingsCapabilities = data.mappingsCapabilities.map((e: any) => new PathDataTableMappingCapabilitiesDto(e.sourceFieldId, e.transformationLogicIds));
            let transformationsCapabilities = data.transformationsCapabilities.map((e: any) => TransformationMapper.ToDto(e));
            return ProcessingResult.Ok(new GetPathDataTableCapabilitiesResult(fields, mappingsCapabilities, transformationsCapabilities));
        }

        return ProcessingResult.Error("Error = " + response.status.toString());
    }
};

export default PathDataTableApi;